/**
 * Utility function to find the nearest resource node of a specific type
 */

export interface ResourceNodeData {
  id: string;
  type: string;
  name: string;
  x: number;
  y: number;
  tileX?: number;
  tileY?: number;
  harvestsRemaining: number;
  maxHarvests: number;
  harvestTime: number;
  requiredLevel?: number;
  requiredSkill?: string;
}

/**
 * Find the nearest resource node of a specific type to the given position
 */
export const findNearestResourceNode = (
  resourceNodes: Map<string, any>,
  playerX: number,
  playerY: number,
  resourceType: string,
): ResourceNodeData | null => {
  let nearestNode: ResourceNodeData | null = null;
  let nearestDistance = Infinity;

  // Iterate through all resource nodes
  resourceNodes.forEach((nodeContainer: any) => {
    // Get the hitArea data which contains the node information
    const hitArea = nodeContainer?.hitArea;
    if (!hitArea) return;

    // Check if this node is the requested type
    if (hitArea.type !== resourceType) return;

    // Skip depleted nodes
    if (hitArea.isDepleted || hitArea.harvestsRemaining <= 0) return;

    // Calculate distance
    const dx = hitArea.x - playerX;
    const dy = hitArea.y - playerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestNode = {
        id: hitArea.id,
        type: hitArea.type,
        name: hitArea.nodeName || `${resourceType.replace(/_/g, ' ')}`,
        x: hitArea.x,
        y: hitArea.y,
        harvestsRemaining: hitArea.harvestsRemaining || 0,
        maxHarvests: hitArea.maxHarvests || 1,
        harvestTime: hitArea.harvestTime || 5000,
        requiredLevel: hitArea.requiredLevel,
        requiredSkill: hitArea.requiredSkill,
      };
    }
  });

  return nearestNode;
};

/**
 * Format resource type for display (convert snake_case to Title Case)
 */
export const formatResourceTypeName = (resourceType: string): string => {
  return resourceType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
